import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { SkeletonBlock } from '../../../components/base/Skeleton';

const MONTHLY_RENT = 34000;
const SECONDS_PER_MONTH = 30 * 24 * 3600;
const PER_SECOND = MONTHLY_RENT / SECONDS_PER_MONTH;
const BASE_INCOME = 2140;
const MAX_INCOME = 30_000_000;
const PRICE_CAP = 1_000_000;

function AnimatedNumber({ value, decimals = 4 }: { value: number; decimals?: number }) {
  const parts = value.toFixed(decimals).split('.');
  const intPart = parseInt(parts[0]).toLocaleString('zh-TW');
  const decPart = parts[1] ?? '0'.repeat(decimals);
  return (
    <span className="tabular-nums">
      {intPart}
      {decimals > 0 && <span className="opacity-40">.{decPart}</span>}
    </span>
  );
}

export default function HeroLiveStats() {
  // ── 月租金模擬 ──
  const [incomeDisplay, setIncomeDisplay] = useState(BASE_INCOME);
  const startRef = useRef(Date.now());
  const rafRef = useRef<number>(0);

  // ── 省下仲介費 ──
  const [savingsTarget, setSavingsTarget] = useState(0);
  const [savingsDisplay, setSavingsDisplay] = useState(0);
  const [savingsLoaded, setSavingsLoaded] = useState(false);
  const countUpRafRef = useRef<number>(0);
  const countUpStartRef = useRef<number>(0);
  const countUpFromRef = useRef<number>(0);

  // 收益模擬（逐幀跑動 + 3000萬防爆）
  useEffect(() => {
    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      setIncomeDisplay((BASE_INCOME + elapsed * PER_SECOND) % MAX_INCOME);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // count-up 動畫：從當前值滾到新目標
  const runCountUp = (from: number, to: number) => {
    cancelAnimationFrame(countUpRafRef.current);
    countUpFromRef.current = from;
    countUpStartRef.current = performance.now();
    const duration = 1600;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min((now - countUpStartRef.current) / duration, 1);
      const val = countUpFromRef.current + (to - countUpFromRef.current) * ease(t);
      setSavingsDisplay(parseFloat(val.toFixed(1)));
      if (t < 1) countUpRafRef.current = requestAnimationFrame(step);
      else setSavingsDisplay(to);
    };
    countUpRafRef.current = requestAnimationFrame(step);
  };

  // 抓資料 + Realtime
  const fetchSavings = async () => {
    const { data } = await supabase.from('properties').select('price');
    if (!data) return;
    const total = data.reduce((acc, p) => acc + Math.min(p.price || 0, PRICE_CAP), 0);
    const wan = parseFloat(((total * 1.5) / 10000).toFixed(1));
    setSavingsTarget(wan);
    setSavingsLoaded(true);
  };

  useEffect(() => {
    fetchSavings();
    const channel = supabase
      .channel('hero_savings_watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'properties' }, fetchSavings)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // 每次目標更新就跑 count-up
  useEffect(() => {
    if (!savingsLoaded) return;
    runCountUp(savingsDisplay, savingsTarget);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingsTarget, savingsLoaded]);

  return (
    <div className="flex flex-col gap-3">

      {/* Card 1：省下仲介費 */}
      <div className="relative rounded-2xl p-[2px] bg-gradient-to-br from-amber-300 via-orange-400 to-yellow-500">
        <div className="bg-gray-950/95 backdrop-blur-md rounded-2xl overflow-hidden">
          <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-500/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="px-4 pt-4 pb-4 relative">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-xs font-black leading-snug">平台預估幫用戶省下的仲介費</p>
                </div>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-400/15 border border-amber-400/30 rounded-full flex-shrink-0">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
                <span className="text-amber-300 text-xs font-bold">即時</span>
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-amber-400 text-base font-black">NT$</span>
              {savingsLoaded ? (
                <span className="text-4xl lg:text-6xl font-black text-white tracking-tight leading-none">
                  <AnimatedNumber value={savingsDisplay} decimals={1} />
                </span>
              ) : (
                <SkeletonBlock className="h-12 w-40 rounded-xl bg-white/10" />
              )}
              <span className="text-white/50 text-xl lg:text-3xl font-black">萬元</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2：月租金收益模擬 */}
      <div className="relative rounded-2xl p-[1.5px] bg-gradient-to-br from-teal-400 via-emerald-400 to-cyan-500">
        <div className="bg-gray-950/90 backdrop-blur-md rounded-2xl overflow-hidden">
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl">
                  <i className="ri-line-chart-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-xs font-black leading-tight">月租金收益模擬</p>
                  <p className="text-teal-400/70 text-xs leading-tight">NT$ 34,000 / 月換算</p>
                </div>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-teal-400/15 rounded-full">
                <div className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-ping"></div>
                <span className="text-teal-300 text-xs font-semibold">即時跳動</span>
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-teal-400 text-base font-bold">$</span>
              <span className="text-3xl lg:text-5xl font-black text-white tracking-tight leading-none">
                <AnimatedNumber value={incomeDisplay} decimals={4} />
              </span>
              <span className="text-white/60 text-lg font-bold">元</span>
            </div>
          </div>
          <div className="px-4 py-2 bg-teal-400/10 border-t border-teal-400/20 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-teal-400 rounded-full"></div>
            <span className="text-teal-400/70 text-xs">每秒 +{PER_SECOND.toFixed(4)} 元</span>
          </div>
        </div>
      </div>

    </div>
  );
}
